import {
  doc,
  getDoc,
  onSnapshot,
  setDoc,
  type Unsubscribe,
} from "firebase/firestore";
import type { UserProfile, UserType } from "@shared/types";
import { db } from "../firebase";

/**
 * User profile document at users/{uid}/profile/main.
 *
 * Stored in a subcollection (not on the user doc directly) to keep
 * rules clean — the /profile/{doc=**} rule gates access independently
 * of /data/** and /settings/**.
 */
export const userProfileRepository = {
  ref(uid: string) {
    return doc(db, "users", uid, "profile", "main");
  },

  watch(
    uid: string,
    onChange: (profile: UserProfile | null, loaded: boolean) => void
  ): Unsubscribe {
    return onSnapshot(
      this.ref(uid),
      (snap) => {
        if (!snap.exists()) {
          onChange(null, true);
          return;
        }
        onChange(snap.data() as UserProfile, true);
      },
      (err) => {
        console.error("[userProfileRepository.watch]", err);
        onChange(null, true);
      }
    );
  },

  async get(uid: string): Promise<UserProfile | null> {
    const snap = await getDoc(this.ref(uid));
    if (!snap.exists()) return null;
    return snap.data() as UserProfile;
  },

  async upsert(uid: string, patch: Partial<UserProfile>): Promise<void> {
    await setDoc(this.ref(uid), patch, { merge: true });
  },

  async setUserType(uid: string, userType: UserType): Promise<void> {
    await this.upsert(uid, { userType });
  },
};
